import React, { useCallback, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { PubSub } from "../../helper/pubSub";
import { useAccordion } from "../../contexts/AccordionContext";
// icons & pngs
import HospitalLogo from "../../img/user/hospital.png";
import {
  BsBellFill,
  BsFiles,
  BsFillPersonFill,
  BsQuestionSquareFill,
} from "react-icons/bs";
import { IoIosDocument, IoMdInformationCircle } from "react-icons/io";
// sidebar
import BasicSidebar from "./BasicSidebar";
import { SideBarItem } from "../../types/sidebarTypes";

const SidebarPatient = () => {
  const { state: appState, handleAccordion, closeAll } = useAccordion();
  const location = useLocation();
  const navigate = useNavigate();

  const generalPaths = ["/impressum", "/privacypolicy", "/contact"];

  const [generalShown, setGeneralShown] = useState<boolean>(
    generalPaths.includes(location.pathname),
  );

  const closeAllAccordions = useCallback(() => {
    setGeneralShown(false);
    closeAll("patient");
  }, [closeAll]);

  const handleSidebarItemClick = useCallback(
    (input: string) => {
      // Ensure General menu is closed
      setGeneralShown(false);

      handleAccordion({ input, userType: "patient" });
      // If not on the patients page, navigate there
      if (location.pathname !== "/patients") {
        navigate("/patients", { replace: true });
      }
    },
    [handleAccordion, location.pathname, navigate],
  );

  // Handlers for context-managed accordions
  const handleCurrent = useCallback(() => {
    handleSidebarItemClick("current");
  }, [handleSidebarItemClick]);

  const handleProfile = () => {
    handleSidebarItemClick("info");
  };

  const handleSurvey = () => {
    handleSidebarItemClick("survey");
  };

  const handleReport = () => {
    handleSidebarItemClick("report");
  };

  const handleClinicItem = () => {
    handleSidebarItemClick("clinics");
  };

  /**
   * Navigates to the information page
   * closes all context-managed accordions.
   */
  const handleInfo = () => {
    setGeneralShown(false);

    navigate("/patients/information", { replace: true });
    closeAll("patient");
  };

  /**
   * Handles the click for the 'Allgemeines' dropdown.
   * Toggles the local 'generalShown' state and closes all context-managed accordions.
   */
  const handleGeneral = () => {
    setGeneralShown((prevState) => !prevState);
    closeAll("patient"); // Close Context accordions
  };

  /**
   * Subscribes to two PubSub topics
   * Cleans up both subscriptions on unmount.
   */
  useEffect(() => {
    // Subscribe to 'open-sidebar-submenu'
    const openSubMenuSubscription = PubSub.getInstance().subscribeToTopic(
      "open-sidebar-submenu",
      ({ menuName }: { menuName: string }) => {
        if (menuName === "current") {
          handleCurrent();
        }
      },
    );

    // Subscribe to 'close-sidebar-submenus'
    const closeSubMenusSubscription = PubSub.getInstance().subscribeToTopic(
      "close-sidebar-submenus",
      closeAllAccordions,
    );

    return () => {
      PubSub.getInstance().unsubscribe(openSubMenuSubscription);
      PubSub.getInstance().unsubscribe(closeSubMenusSubscription);
    };
  }, [handleCurrent, closeAllAccordions]);

  // Derived active states from Context and location
  const isCurrentOpen =
    appState.patient?.current !== "-1" && location.pathname === "/patients";
  const isProfileOpen =
    appState.patient?.info !== "-1" && location.pathname === "/patients";
  const isSurveyOpen =
    appState.patient?.survey !== "-1" && location.pathname === "/patients";
  const isReportOpen =
    appState.patient?.report !== "-1" && location.pathname === "/patients";
  const isClinicOpen =
    appState.patient?.clinics !== "-1" && location.pathname === "/patients";
  const isInfoOpen = location.pathname === "/patients/information";

  const currentSidebarItem: SideBarItem = {
    title: "Aktuelles",
    activeButton: isCurrentOpen,
    onClickFunction: handleCurrent,
    imgSrc: <BsBellFill size={25} />,
  };

  const profileSidebarItem: SideBarItem = {
    title: "Profil",
    activeButton: isProfileOpen,
    onClickFunction: handleProfile,
    imgSrc: <BsFillPersonFill size={25} />,
  };

  const surveySidebarItem: SideBarItem = {
    title: "Befragungen",
    activeButton: isSurveyOpen,
    onClickFunction: handleSurvey,
    imgSrc: <BsQuestionSquareFill size={25} />,
  };

  const reportsSidebarItem: SideBarItem = {
    title: "Berichte",
    activeButton: isReportOpen,
    onClickFunction: handleReport,
    imgSrc: <BsFiles size={25} />,
  };

  const clinicSidebarItem: SideBarItem = {
    title: "Ambulanz",
    activeButton: isClinicOpen,
    onClickFunction: handleClinicItem,
    imgSrc: HospitalLogo,
  };

  const infoSidebarItem: SideBarItem = {
    title: "Informationen",
    activeButton: isInfoOpen,
    onClickFunction: handleInfo,
    imgSrc: <IoMdInformationCircle size={25} />,
    buttonId: "sidenav-link",
  };

  const generalSidebarItem: SideBarItem = {
    title: "Allgemeines",
    activeButton: generalShown,
    onClickFunction: handleGeneral,
    imgSrc: <IoIosDocument size={25} />,
    buttonId: "clinic-stuff-sidebar-information",
    ariaExpand: generalShown,
    ariaControl: "documentation",
    hiddenOnMobile: true,
    subMenu: [
      {
        title: "Impressum",
        redirectLink: "/impressum",
      },
      {
        title: "Datenschutz",
        redirectLink: "/privacypolicy",
      },
      {
        title: "Kontakt",
        redirectLink: "/contact",
      },
    ],
  };

  return (
    <BasicSidebar
      sidebarItems={[
        currentSidebarItem,
        profileSidebarItem,
        surveySidebarItem,
        reportsSidebarItem,
        clinicSidebarItem,
        "---",
        infoSidebarItem,
        generalSidebarItem,
      ]}
    />
  );
};

export default SidebarPatient;
